import { useState } from "react";
import { mockProducts, mockIbanList, mockCurrentDealers } from "@/mocks/dealerData";
import { usePurchasePrice } from "@/contexts/PurchasePriceContext";

export default function SalesPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<typeof mockProducts>([]);
  const [cart, setCart] = useState<Array<{ product: (typeof mockProducts)[0]; quantity: number; customPrice: number; description?: string }>>([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [selectedIban, setSelectedIban] = useState<string | null>(null);
  const [selectedDealer, setSelectedDealer] = useState<string | null>(null);
  const [dealerSearch, setDealerSearch] = useState("");
  const [saleComplete, setSaleComplete] = useState(false);
  const [showNewDealerModal, setShowNewDealerModal] = useState(false);
  const [newDealer, setNewDealer] = useState({ name: "", phone: "", address: "" });

  const filteredDealers = dealerSearch.trim()
    ? mockCurrentDealers.filter((d) => d.name.toLowerCase().includes(dealerSearch.toLowerCase()))
    : mockCurrentDealers;
  const { showPurchasePrice } = usePurchasePrice();

  // Out-of-stock warning state
  const [outOfStockProduct, setOutOfStockProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [outOfStockDescription, setOutOfStockDescription] = useState("");
  const [showOutOfStockModal, setShowOutOfStockModal] = useState(false);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    const q = value.toLowerCase().trim();
    if (!q) {
      setResults([]);
      return;
    }
    const filtered = mockProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.productCode.toLowerCase().includes(q) ||
        p.barcode.includes(q) ||
        p.brand.toLowerCase().includes(q)
    );
    setResults(filtered);
  };

  const addToCart = (product: (typeof mockProducts)[0]) => {
    // Check if product is out of stock
    if ((product.stock || 0) <= 0) {
      setOutOfStockProduct(product);
      setOutOfStockDescription("");
      setShowOutOfStockModal(true);
      return;
    }
    addProductToCart(product);
  };

  const addProductToCart = (product: (typeof mockProducts)[0], description?: string) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1, customPrice: product.salePrice, description }];
    });
  };

  const confirmOutOfStockSale = () => {
    if (!outOfStockProduct || !outOfStockDescription.trim()) return;
    addProductToCart(outOfStockProduct, outOfStockDescription.trim());
    setShowOutOfStockModal(false);
    setOutOfStockProduct(null);
    setOutOfStockDescription("");
  };

  const cancelOutOfStock = () => {
    setShowOutOfStockModal(false);
    setOutOfStockProduct(null);
    setOutOfStockDescription("");
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) { removeFromCart(productId); return; }
    setCart((prev) => prev.map((item) => item.product.id === productId ? { ...item, quantity } : item));
  };

  const updateCustomPrice = (productId: string, price: number) => {
    setCart((prev) => prev.map((item) => item.product.id === productId ? { ...item, customPrice: price } : item));
  };

  const totalAmount = cart.reduce((sum, item) => sum + item.customPrice * item.quantity, 0);

  const resetSale = () => {
    setCart([]);
    setPaymentMethod(null);
    setSelectedIban(null);
    setSelectedDealer(null);
    setDealerSearch("");
    setShowCheckout(false);
    setSaleComplete(false);
  };

  const confirmSale = () => {
    if (!paymentMethod) return;
    if (paymentMethod === "iban" && !selectedIban) return;
    if (paymentMethod === "cari" && !selectedDealer) return;
    setSaleComplete(true);
    setShowCheckout(false);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="sales-title">Satış Terminali</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Ürün arayın, sepete ekleyin ve satışı tamamlayın.</p>
        </div>
        {saleComplete && (
          <button onClick={resetSale} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Yeni Satış
          </button>
        )}
      </div>

      {saleComplete ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-primary-600 text-3xl"></i>
          </div>
          <h4 className="text-xl font-semibold text-foreground-950 mb-2">Satış Başarıyla Tamamlandı!</h4>
          <p className="text-foreground-500 mb-1">Toplam Tutar: <strong className="text-foreground-950">${totalAmount.toLocaleString("en-US")}</strong></p>
          <p className="text-sm text-foreground-400">Ödeme: {paymentMethod === "nakit" ? "Nakit" : paymentMethod === "pos" ? "POS" : paymentMethod === "iban" ? "IBAN Havale" : "Cari Hesap"}{selectedDealer && (() => { const d = mockCurrentDealers.find(x => x.id === selectedDealer); return d ? ` · Bayi: ${d.name}` : ""; })()}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Product Search & Results */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder="Ürün adı, barkod veya ürün kodu ile arayın..."
                  className="w-full pl-10 pr-4 py-3 lg:py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            {query.trim() !== "" && results.length === 0 && (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center py-10 text-foreground-400">
                <i className="ri-search-line text-4xl block mb-3"></i>
                <p className="text-sm">Aradığınız kriterlere uygun ürün bulunamadı.</p>
              </div>
            )}

            {results.length > 0 && (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-2 max-h-[500px] overflow-y-auto">
                {results.map((product) => (
                  <div key={product.id} onClick={() => addToCart(product)} className="flex items-center gap-3 p-3 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer border border-transparent hover:border-primary-200 active:bg-primary-50">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} title={product.name} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                        <i className="ri-image-line text-foreground-300"></i>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground-900 truncate">{product.name}</p>
                        {(product.stock || 0) <= 0 && (
                          <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded-full font-medium whitespace-nowrap">Stok Yok</span>
                        )}
                        {(product.stock || 0) > 0 && (product.stock || 0) <= 5 && (
                          <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] rounded-full font-medium whitespace-nowrap">Son {product.stock}</span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-400">{product.brand} {product.model} | {product.productCode}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-primary-600">${product.salePrice.toLocaleString("en-US")}</p>
                      {showPurchasePrice && <p className="text-xs text-foreground-400">Alış: ${product.purchasePrice.toLocaleString("en-US")}</p>}
                    </div>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${(product.stock || 0) <= 0 ? "bg-accent-500" : "bg-primary-500"}`}>
                      <i className={`${(product.stock || 0) <= 0 ? "ri-alert-line" : "ri-add-line"} text-background-50`}></i>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Cart - Desktop only */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 sticky top-20">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-shopping-cart-2-line text-lg text-foreground-700"></i>
                <h4 className="font-semibold text-foreground-950 text-sm"><a id="cart-title">Sepet</a></h4>
                <span className="ml-auto text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">{cart.length} ürün</span>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-10 text-foreground-400">
                  <i className="ri-shopping-cart-line text-3xl block mb-2"></i>
                  <p className="text-sm">Sepet boş</p>
                  <p className="text-xs mt-1">Ürün arayıp ekleyin</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto mb-3">
                  {cart.map((item) => (
                    <CartItem key={item.product.id} item={item} updateQuantity={updateQuantity} updateCustomPrice={updateCustomPrice} removeFromCart={removeFromCart} showPurchasePrice={showPurchasePrice} />
                  ))}
                </div>
              )}

              {cart.length > 0 && (
                <>
                  <div className="border-t border-background-200/70 pt-3 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground-950">Toplam</span>
                      <span className="text-lg font-bold text-foreground-950">${totalAmount.toLocaleString("en-US")}</span>
                    </div>
                  </div>
                  <button onClick={() => setShowCheckout(true)} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    Satışı Tamamla
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile Floating Cart Button */}
          {cart.length > 0 && (
            <div className="lg:hidden fixed bottom-20 left-4 right-4 z-30">
              <button
                onClick={() => setShowCheckout(true)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-2xl shadow-lg transition-all cursor-pointer active:scale-[0.98]"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <i className="ri-shopping-cart-2-line text-xl"></i>
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-background-50 text-primary-600 text-[11px] font-bold rounded-full flex items-center justify-center">{cart.length}</span>
                  </div>
                  <span className="text-sm font-semibold">Sepeti Gör</span>
                </div>
                <span className="text-sm font-bold">${totalAmount.toLocaleString("en-US")}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/30" onClick={() => setShowCheckout(false)}>
          <div className="bg-background-50 rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-md mx-0 lg:mx-4 max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950"><a id="payment-method">Ödeme Yöntemi</a></h4>
                <p className="text-xs text-foreground-500 mt-0.5">{cart.length} ürün · ${totalAmount.toLocaleString("en-US")}</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Cart Items Summary (compact) */}
            <div className="bg-background-100 rounded-xl p-3 mb-4 max-h-40 overflow-y-auto space-y-2">
              {cart.map((item) => (
                <div key={item.product.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="w-6 h-6 rounded bg-background-50 border border-background-200/70 flex items-center justify-center text-xs font-medium text-foreground-600 shrink-0">{item.quantity}x</span>
                    <span className="truncate text-foreground-900">{item.product.name}</span>
                  </div>
                  <span className="font-medium text-foreground-900 shrink-0">${(item.customPrice * item.quantity).toLocaleString("en-US")}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2 mb-4">
              {[{ id: "nakit", label: "Nakit", icon: "ri-money-dollar-circle-line" }, { id: "pos", label: "POS / Kredi Kartı", icon: "ri-bank-card-line" }, { id: "iban", label: "IBAN Havale", icon: "ri-bank-line" }, { id: "cari", label: "Cari Hesap", icon: "ri-user-star-line" }].map((m) => (
                <button key={m.id} onClick={() => { setPaymentMethod(m.id); setSelectedIban(null); setSelectedDealer(null); setDealerSearch(""); }} className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${paymentMethod === m.id ? "bg-primary-100 border border-primary-200 text-primary-700" : "bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70"}`}>
                  <i className={`${m.icon} text-lg`}></i> {m.label}
                  {paymentMethod === m.id && <i className="ri-check-line ml-auto"></i>}
                </button>
              ))}
            </div>

            {paymentMethod === "iban" && (
              <div className="mb-4">
                <p className="text-xs font-medium text-foreground-500 mb-2">İşlem yapılacak IBAN'ı seçin</p>
                <div className="space-y-1.5">
                  {mockIbanList.map((iban) => (
                    <button key={iban.id} onClick={() => setSelectedIban(iban.id)} className={`w-full text-left p-2.5 rounded-lg text-xs transition-colors cursor-pointer ${selectedIban === iban.id ? "bg-primary-100 border border-primary-200" : "bg-background-100 border border-background-200/70 hover:bg-background-200/70"}`}>
                      <span className="font-medium text-foreground-700">{iban.bankName}</span>
                      <span className="text-foreground-400 ml-2">{iban.ibanNumber}</span>
                    </button>
                  ))}
                </div>
                {selectedIban === null && <p className="text-xs text-accent-500 mt-1">Lütfen bir IBAN seçin</p>}
              </div>
            )}

            {paymentMethod && (
              <div className="mb-4">
                <p className="text-xs font-medium text-foreground-500 mb-2">Cari bayi ara</p>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    value={dealerSearch}
                    onChange={(e) => { setDealerSearch(e.target.value); setSelectedDealer(null); }}
                    placeholder="Bayi adı yazarak arayın..."
                    className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {selectedDealer && (() => {
                  const dealer = mockCurrentDealers.find((d) => d.id === selectedDealer);
                  if (!dealer) return null;
                  return (
                    <div className="mt-2 p-2.5 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-user-line text-primary-600 text-sm"></i>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-foreground-900">{dealer.name}</p>
                          <p className="text-[11px] text-foreground-500">Bakiye: ${dealer.balance.toLocaleString("en-US")}</p>
                        </div>
                      </div>
                      <button onClick={() => { setSelectedDealer(null); setDealerSearch(""); }} className="w-6 h-6 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-400 hover:text-accent-500 cursor-pointer">
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  );
                })()}

                {!selectedDealer && dealerSearch.trim() && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {filteredDealers.length > 0 ? (
                      filteredDealers.map((dealer) => (
                        <button
                          key={dealer.id}
                          onClick={() => { setSelectedDealer(dealer.id); setDealerSearch(dealer.name); }}
                          className="w-full text-left p-2.5 rounded-lg text-xs transition-colors cursor-pointer bg-background-100 border border-background-200/70 hover:bg-primary-50 hover:border-primary-200"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground-700">{dealer.name}</span>
                            <span className="text-foreground-400">Bakiye: ${dealer.balance.toLocaleString("en-US")}</span>
                          </div>
                          <p className="text-[11px] text-foreground-400 mt-0.5">{dealer.phone} · {dealer.address}</p>
                        </button>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-xs text-foreground-400 mb-2">"{dealerSearch}" ile eşleşen bayi bulunamadı.</p>
                        <button
                          onClick={() => setShowNewDealerModal(true)}
                          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line"></i> Yeni Cari Bayi Ekle
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!selectedDealer && !dealerSearch.trim() && paymentMethod !== "cari" && (
                  <div className="mt-2 p-3 bg-background-100 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-foreground-500">Bayi seçmeden devam edebilir veya arama yapabilirsiniz.</p>
                    </div>
                    <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                      {mockCurrentDealers.map((dealer) => (
                        <button
                          key={dealer.id}
                          onClick={() => { setSelectedDealer(dealer.id); setDealerSearch(dealer.name); }}
                          className="w-full text-left p-2.5 rounded-lg text-xs transition-colors cursor-pointer bg-background-50 border border-background-200/70 hover:bg-primary-50 hover:border-primary-200"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground-700">{dealer.name}</span>
                            <span className="text-foreground-400">Bakiye: ${dealer.balance.toLocaleString("en-US")}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowNewDealerModal(true)}
                      className="mt-2 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i> Yeni Cari Ekle
                    </button>
                  </div>
                )}

                {!selectedDealer && !dealerSearch.trim() && paymentMethod === "cari" && (
                  <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                    {mockCurrentDealers.map((dealer) => (
                      <button
                        key={dealer.id}
                        onClick={() => { setSelectedDealer(dealer.id); setDealerSearch(dealer.name); }}
                        className="w-full text-left p-2.5 rounded-lg text-xs transition-colors cursor-pointer bg-background-100 border border-background-200/70 hover:bg-primary-50 hover:border-primary-200"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-foreground-700">{dealer.name}</span>
                          <span className="text-foreground-400">Bakiye: ${dealer.balance.toLocaleString("en-US")}</span>
                        </div>
                      </button>
                    ))}
                    <button
                      onClick={() => setShowNewDealerModal(true)}
                      className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i> Yeni Cari
                    </button>
                  </div>
                )}
              </div>
            )}

            <button onClick={confirmSale} disabled={!paymentMethod || (paymentMethod === "iban" && !selectedIban) || (paymentMethod === "cari" && !selectedDealer)} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
              Satışı Onayla (${totalAmount.toLocaleString("en-US")})
            </button>
          </div>
        </div>
      )}

      {/* New Dealer Modal */}
      {showNewDealerModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={() => setShowNewDealerModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="new-dealer">Yeni Cari Bayi</a></h4>
              <button onClick={() => setShowNewDealerModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Bayi Adı</label>
                <input type="text" value={newDealer.name} onChange={(e) => setNewDealer({ ...newDealer, name: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="Bayi adı" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Telefon</label>
                <input type="text" value={newDealer.phone} onChange={(e) => setNewDealer({ ...newDealer, phone: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="Telefon" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Adres</label>
                <input type="text" value={newDealer.address} onChange={(e) => setNewDealer({ ...newDealer, address: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="Adres" />
              </div>
              <button onClick={() => { if (newDealer.name) { setSelectedDealer("new"); setShowNewDealerModal(false); } }} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Out of Stock Warning Modal */}
      {showOutOfStockModal && outOfStockProduct && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={cancelOutOfStock}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">
                <a id="out-of-stock-warning">Stok Uyarısı</a>
              </h4>
              <button onClick={cancelOutOfStock} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="bg-accent-50 border border-accent-200/70 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950 mb-1">{outOfStockProduct.name}</p>
                  <p className="text-sm text-foreground-600">
                    Bu ürün şu anda <strong className="text-accent-600">stokta mevcut değil.</strong>
                  </p>
                  <p className="text-sm text-foreground-500 mt-1">
                    Satış yapmak istiyor musunuz?
                  </p>
                </div>
              </div>
            </div>

            {outOfStockDescription !== "" ? (
              /* Step 2: Description input shown after clicking Yes */
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">
                    Satış Açıklaması <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    value={outOfStockDescription}
                    onChange={(e) => setOutOfStockDescription(e.target.value)}
                    placeholder="Stokta olmayan ürünün neden satıldığını açıklayın (örn: tedarikçiden sipariş verildi, müşteriye özel sipariş...)"
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                    autoFocus
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">{outOfStockDescription.length}/500</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={cancelOutOfStock} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                    İptal
                  </button>
                  <button
                    onClick={confirmOutOfStockSale}
                    disabled={!outOfStockDescription.trim()}
                    className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    Satış Yap
                  </button>
                </div>
              </div>
            ) : (
              /* Step 1: Yes/No prompt */
              <div className="flex gap-2">
                <button onClick={cancelOutOfStock} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                  Hayır
                </button>
                <button
                  onClick={() => setOutOfStockDescription("")}
                  className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  Evet
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CartItem({ item, updateQuantity, updateCustomPrice, removeFromCart, showPurchasePrice }: {
  item: { product: (typeof mockProducts)[0]; quantity: number; customPrice: number; description?: string };
  updateQuantity: (productId: string, quantity: number) => void;
  updateCustomPrice: (productId: string, price: number) => void;
  removeFromCart: (productId: string) => void;
  showPurchasePrice: boolean;
}) {
  return (
    <div className="bg-background-100 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground-900 flex-1 truncate">{item.product.name}</span>
        {(item.product.stock || 0) <= 0 && (
          <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded-full font-medium whitespace-nowrap">Stok Yok</span>
        )}
        <button onClick={() => removeFromCart(item.product.id)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer shrink-0">
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>
      {item.description && (
        <div className="bg-accent-50 border border-accent-200/70 rounded-md px-2 py-1.5">
          <p className="text-xs text-accent-700 font-medium mb-0.5">
            <i className="ri-information-line text-xs mr-1"></i>
            Stokta yok - Açıklamalı Satış
          </p>
          <p className="text-xs text-foreground-600">{item.description}</p>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-8 h-8 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer">
            <i className="ri-subtract-line text-sm"></i>
          </button>
          <span className="w-7 text-center text-sm font-medium text-foreground-900">{item.quantity}</span>
          <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-8 h-8 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer">
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-foreground-400">$</span>
          <input
            type="number"
            value={item.customPrice}
            onChange={(e) => updateCustomPrice(item.product.id, Number(e.target.value))}
            className="w-20 text-right text-sm font-medium text-foreground-900 bg-transparent border border-background-200/70 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>
      {showPurchasePrice && <p className="text-xs text-foreground-400">Alış: ${item.product.purchasePrice.toLocaleString("en-US")}</p>}
    </div>
  );
}